import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScheduleHistoryQueryByDate } from '@principle-theorem/ng-principle-shared';
import {
  SchedulingEvent,
  TimezoneResolver,
} from '@principle-theorem/principle-core';
import {
  IPractice,
  ISchedulingEventSummary,
  MentionResourceType,
  SchedulingEventType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  ISODateType,
  ITimePeriod,
  WithRef,
  filterUndefined,
  getDaysInPeriod,
  isSameRef,
  multiSort,
  multiSortBy$,
  safeCombineLatest,
  sortTimestamp,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  merge,
} from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export interface ISchedulingEventQuery {
  practiceRef: DocumentReference<IPractice>;
  eventTypes: SchedulingEventType[];
  byDate: ScheduleHistoryQueryByDate;
  dateRange: ITimePeriod;
  filterAcknowledged: boolean;
  scheduledByType?: MentionResourceType[];
}

interface IDayQuery {
  query: ISchedulingEventQuery;
  date: ISODateType;
}

export interface IDayResult {
  date: ISODateType;
  events: WithRef<ISchedulingEventSummary>[];
}

// TODO: Convert to signals
@Injectable()
export class ScheduleHistoryService {
  private _snackBar = inject(MatSnackBar);
  loading$ = new BehaviorSubject<boolean>(false);
  query$ = new BehaviorSubject<ISchedulingEventQuery | undefined>(undefined);
  filter$ = new BehaviorSubject<string>('');
  days$: Observable<IDayResult[]>;
  hasMultipleDays$: Observable<boolean>;
  removeResult$ = new Subject<WithRef<ISchedulingEventSummary>>();

  constructor() {
    const queryResults$ = this.query$.pipe(
      filterUndefined(),
      tap(() => this.loading$.next(true)),
      switchMap((query) => this._getDayQueries(query)),
      switchMap((dayQueries) => this._resolveDayQueries$(dayQueries)),
      tap(() => this.loading$.next(false))
    );

    const removeResult$ = combineLatest([
      queryResults$,
      this.removeResult$,
    ]).pipe(
      map(([results, removeResult]) =>
        results.map((day) => ({
          ...day,
          events: day.events.filter((event) => !isSameRef(event, removeResult)),
        }))
      )
    );

    this.days$ = combineLatest([
      merge(queryResults$, removeResult$),
      this.filter$,
    ]).pipe(
      map(([queryData, filterData]) =>
        this._applyFilters(queryData, filterData)
      )
    );

    this.hasMultipleDays$ = this.days$.pipe(map((days) => days.length > 1));
  }

  async acknowledgeAppointmentEvent(
    event: WithRef<ISchedulingEventSummary>
  ): Promise<void> {
    await Firestore.patchDoc(event.schedulingEventRef, {
      acknowleged: true,
    });
    this.removeResult$.next(event);
    this._snackBar.open('Appointment event dismissed');
  }

  private async _getDayQueries(
    query: ISchedulingEventQuery
  ): Promise<IDayQuery[]> {
    const timezone = await TimezoneResolver.fromPracticeRef(query.practiceRef);
    return getDaysInPeriod(timezone, query.dateRange).map((date) => ({
      date,
      query,
    }));
  }

  private _resolveDayQueries$(
    dayQueries: IDayQuery[]
  ): Observable<IDayResult[]> {
    const groups$ = dayQueries.map((dayQuery) =>
      this._resolveDayQuery$(dayQuery)
    );
    return safeCombineLatest(groups$).pipe(multiSortBy$((group) => group.date));
  }

  private _resolveDayQuery$(dayQuery: IDayQuery): Observable<IDayResult> {
    return this._getEvents$(
      dayQuery.query.practiceRef,
      dayQuery.query.eventTypes,
      dayQuery.query.byDate,
      dayQuery.query.filterAcknowledged,
      dayQuery.date,
      dayQuery.query.scheduledByType
    ).pipe(
      multiSort((a, b) => sortTimestamp(a.scheduledAt, b.scheduledAt)),
      map((events) => ({ date: dayQuery.date, events }))
    );
  }

  private _getEvents$(
    practiceRef: DocumentReference<IPractice>,
    eventTypes: SchedulingEventType[],
    byDate: ScheduleHistoryQueryByDate,
    filterAcknowledged: boolean,
    isoDate: string,
    scheduledByType: MentionResourceType[] = []
  ): Observable<WithRef<ISchedulingEventSummary>[]> {
    if (byDate === ScheduleHistoryQueryByDate.AffectsDate) {
      return SchedulingEvent.queryByAffectsPractice$(
        practiceRef,
        eventTypes,
        filterAcknowledged,
        isoDate,
        scheduledByType
      );
    }
    return SchedulingEvent.queryByScheduledByPractice$(
      practiceRef,
      eventTypes,
      filterAcknowledged,
      isoDate,
      scheduledByType
    );
  }

  private _applyFilters(days: IDayResult[], filterData: string): IDayResult[] {
    return days.map((day) => ({
      date: day.date,
      events: this._filterEvents(day.events, filterData),
    }));
  }

  private _filterEvents(
    events: WithRef<ISchedulingEventSummary>[],
    filterValue: string
  ): WithRef<ISchedulingEventSummary>[] {
    if (!filterValue.length) {
      return events;
    }
    const searchTerms = filterValue.toLowerCase().split(' ');
    return events.filter((event) => {
      const hasAllTerms = searchTerms.every((term) =>
        event.filterValue.includes(term)
      );
      return hasAllTerms;
    });
  }
}
